import { useEffect, useState } from 'react';
import { highlight, type ShikiLang } from '@/lib/shiki';
import { useTheme } from '@/store/theme';

/**
 * Syntax-highlighted code block via Shiki.
 *
 * Renders a plain `<pre>` while Shiki initialises (~200ms first time), then
 * swaps in the highlighted HTML. Theme follows the global theme store.
 */
export function CodeBlock({
  code,
  language,
  className,
}: {
  code: string;
  language: ShikiLang;
  className?: string;
}) {
  const theme = useTheme();
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    highlight(code, language, theme === 'dark' ? 'github-dark-default' : 'github-light')
      .then((out) => {
        if (!cancelled) setHtml(out);
      })
      .catch(() => {
        if (!cancelled) setHtml(null);
      });
    return () => {
      cancelled = true;
    };
  }, [code, language, theme]);

  if (html) {
    return (
      <div
        className={className}
        // Shiki output is trusted (its own escaping); themed via inline styles.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return (
    <pre className={className}>
      <code>{code}</code>
    </pre>
  );
}
